import {useQuery} from '@tanstack/react-query'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

async function fetchSlotCalloutMessage(slotID, context, appOrigin, siteId) {
    try {
        // Validate required params
        if (!slotID || !context) {
            logger.error('Missing required parameters for fetching content slot', {
                namespace: 'fetchContentSlot',
                additionalProperties: {slotID, context}
            })
            return null
        }

        const params = {
            slotID: slotID,
            context: context,
            siteId: siteId
        }
        const queryString = new URLSearchParams(params).toString()
        const response = await fetch(`${appOrigin}/slot-callout?${queryString}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        })

        if (!response.ok) {
            throw new Error('Failed to Fetch Content Slot')
        }

        const slotJson = await response.json()

        if (slotJson?.data?.error) {
            logger.error('Error while fetching Content Slot', {
                namespace: 'fetchContentSlot',
                additionalProperties: {error: slotJson?.data?.error}
            })
            return null
        }

        if (slotJson?.data?.slot_configurations) {
            const enabledConfigs = slotJson.data.slot_configurations.filter((c) => c?.enabled)
            const match =
                enabledConfigs.length > 1
                    ? enabledConfigs.find((c) => c?.default) || enabledConfigs[0]
                    : enabledConfigs[0]

            return match?.callout_msg?.default?.source || ''
        }

        return ''
    } catch (error) {
        logger.error('Error while fetching Content Slot', {
            namespace: 'fetchContentSlot',
            additionalProperties: {error: error.message}
        })
        return null
    }
}

/**
 * Hook to get slot config
 */
export default function useSlotCalloutMessage(slotID, context) {
    const appOrigin = useAppOrigin()
    const {site} = useMultiSite()
    const siteId = site.id
    const {data: contentSlotConfig, isLoading} = useQuery(
        ['ContentSlotConfig', slotID, context],
        () => fetchSlotCalloutMessage(slotID, context, appOrigin, siteId),
        {
            enabled: Boolean(slotID && context),
            retry: false
        }
    )

    return {
        data: isLoading ? undefined : contentSlotConfig,
        isLoading
    }
}
